/* ============================================================
   Wishlist Context
   ------------------------------------------------------------
   Storage strategy (V1 — device-local, per-user):
     - Key format: `wishlist:<user-email>`
     - Same email + same device = same wishlist (as requested).
   To upgrade to server-side sync later, swap the localStorage
   read / write in `load()` and `persist()` with API calls
   (e.g. GET/POST /api/wishlist). Everything else stays the same.
   ============================================================ */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { trackWishlist } from "../lib/track";

const WishlistContext = createContext(null);

const keyFor = (email) => `wishlist:${(email || "guest").toLowerCase()}`;

const load = (email) => {
  try {
    const raw = localStorage.getItem(keyFor(email));
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    console.warn("Wishlist: failed to load from storage:", err?.message || err);
    return [];
  }
};

const persist = (email, list) => {
  try {
    localStorage.setItem(keyFor(email), JSON.stringify(list));
  } catch (err) {
    console.warn("Wishlist: failed to save to storage:", err?.message || err);
  }
};

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (user && user.email) setItems(load(user.email));
    else setItems([]);
  }, [user]);

  const isSaved = useCallback((id) => items.some((it) => it.id === id), [items]);

  const add = useCallback(
    (design) => {
      if (!user) return;
      setItems((prev) => {
        if (prev.some((it) => it.id === design.id)) return prev;
        // store only the fields we need on the wishlist page
        const slim = {
          id: design.id,
          number: design.number,
          name: design.name,
          styleName: design.styleName,
          image: design.image,
          video: design.video,
          weight: design.weight,
          weightUnit: design.weightUnit,
          purity: design.purity,
          category: design.category,
          subCategory: design.subCategory,
          addedAt: Date.now(),
        };
        const next = [slim, ...prev];
        persist(user.email, next);
        trackWishlist(design, "add");
        return next;
      });
    },
    [user]
  );

  const remove = useCallback(
    (id) => {
      setItems((prev) => {
        const removed = prev.find((it) => it.id === id);
        const next = prev.filter((it) => it.id !== id);
        if (user) persist(user.email, next);
        if (removed) trackWishlist(removed, "remove");
        return next;
      });
    },
    [user]
  );

  const toggle = useCallback(
    (design) => {
      if (isSaved(design.id)) remove(design.id);
      else add(design);
    },
    [isSaved, add, remove]
  );

  const clear = useCallback(() => {
    setItems([]);
    if (user) persist(user.email, []);
  }, [user]);

  const value = useMemo(
    () => ({ items, count: items.length, isSaved, add, remove, toggle, clear }),
    [items, isSaved, add, remove, toggle, clear]
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error("useWishlist must be used inside <WishlistProvider>");
  return ctx;
}
